import { useQuery } from "@apollo/client";
import { gqlCorporateValues } from "gql";
import type {
  IVendorUserList,
  IVendorUserListResponse,
} from "gql/CorporateValues/queries";
import { useMemo } from "react";
import { useUserStore } from "stores/user/userStore";

/**
 * Single source of truth for the "list of people in this vendor" used by the
 * chat (NewChatSearch, ChatList "Nuevo chat", CreateGroupModal,
 * AddParticipantsModal).
 *
 * It piggy-backs on the exact same query the Explore side panel fires on page
 * load (`vendorUsersListWithoutUserLogin`), so when the user opens the chat we
 * don't refetch — Apollo serves it from cache. If the chat is the first thing
 * touched, this hook is the one that warms the cache for everyone else.
 *
 * The query is variable-less, so Apollo's normalised cache hit is automatic
 * (no key juggling). The backend already excludes the logged-in user; we keep
 * a defensive client-side filter for safety.
 */
export const useChatUserDirectory = () => {
  const { user } = useUserStore();

  const { data, loading, error } = useQuery<IVendorUserListResponse>(
    gqlCorporateValues.queries.GET_SIMPLE_VENDOR_USER_LIST,
    {
      fetchPolicy: "cache-first",
      // Avoid hard-failing the UI on partial server errors.
      errorPolicy: "all",
    }
  );

  const users: IVendorUserList[] = useMemo(() => {
    const all = data?.vendorUsersListWithoutUserLogin ?? [];
    return all.filter((u) => u?.id && u.id !== user?.id);
  }, [data?.vendorUsersListWithoutUserLogin, user?.id]);

  return { users, loading, error };
};

/**
 * Client-side fuzzy-ish filter that matches the user-typed search against
 * `${name} ${lastname}` or `email`. Case-insensitive, trims whitespace.
 */
export const filterUsersByText = <
  T extends { name?: string; lastname?: string; email?: string },
>(
  users: T[],
  text: string
): T[] => {
  const q = text.trim().toLowerCase();
  if (!q) return users;
  return users.filter((u) => {
    const name = `${u.name || ""} ${u.lastname || ""}`.toLowerCase();
    const email = (u.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });
};
